import { useRolePrefix } from "@/components/client-role";
import { clientApi } from "@/lib/client-api";
import { cn } from "@/lib/utils";
import type { SearchIndexCandidate, SearchIndexRole, Tier } from "@ow/shared";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Command } from "cmdk";
import {
  Activity,
  AlertTriangle,
  Briefcase,
  CornerDownLeft,
  Layers,
  LayoutGrid,
  Search,
  User,
  Users,
} from "lucide-react";
import { Dialog as DialogPrimitive } from "radix-ui";
import { useCallback, useEffect, useMemo, useState } from "react";

// Global ⌘K search. The home/role search boxes FILTER a list in place; this one NAVIGATES — jump to any
// role or candidate from anywhere. The whole client dataset is tiny (dozens of records) and already
// client-observed, so the index is fetched once and matched on the client (no per-keystroke server call).
// A candidate result routes into the existing role drawer (`/search/$searchId?candidate=$id`) — the one
// place a candidate is viewable — so back-button / Esc semantics come free.

const TIER_LABEL: Record<Tier, string> = { csuite: "C-Suite", vp_director: "VP / Director", general: "General" };

const roleSub = (r: SearchIndexRole) => `${TIER_LABEL[r.tier]}${r.ownerName ? ` · ${r.ownerName}` : ""}`;
const candSub = (c: SearchIndexCandidate) => [c.currentTitle, c.currentEmployer].filter(Boolean).join(" · ") || "—";

/** A recently-opened result, persisted so an empty palette still offers a useful starting point. */
type RecentEntry = { kind: "role" | "candidate"; id: string; searchId: string; title: string; subtitle: string };

const RECENTS_KEY = "ow:recent-search";
const RECENTS_MAX = 6;

function readRecents(): RecentEntry[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as RecentEntry[]) : [];
  } catch {
    return [];
  }
}
function writeRecents(entries: RecentEntry[]): void {
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(entries));
  } catch {
    // storage unavailable — recents are a nicety, not a requirement
  }
}

const ITEM_CLS =
  "flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 text-sm outline-none data-[selected=true]:bg-accent";

/** The command palette + its top-bar trigger, self-contained so `Shell` just drops one `<CommandPalette />`
 *  into the bar. Owns the open state and the global ⌘/Ctrl+K listener; the dialog portals to the body. */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [recents, setRecents] = useState<RecentEntry[]>(() => readRecents());

  const navigate = useNavigate();
  const prefix = useRolePrefix();

  // Global ⌘K / Ctrl+K to toggle. Safe to fire from inputs (convention) — we don't bind bare "/", so the
  // page's own search boxes keep it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const onOpenChange = useCallback((o: boolean) => {
    setOpen(o);
    setQuery(""); // every open starts fresh
  }, []);

  // One deterministic destination for both kinds: role → its detail page; candidate → the role drawer
  // that carries them (`?candidate=`). The `unknown` bridge retypes navigate to the shared role-tree
  // path shape (both trees register `/search/$searchId`), mirroring how RoleLink opts out of per-tree
  // inference — the runtime `to`/`params`/`search` are valid in either tree.
  const go = useCallback(
    (searchId: string, candidateId?: string) => {
      const nav = navigate as unknown as (o: {
        to: string;
        params: { searchId: string };
        search?: { candidate: string };
      }) => void;
      nav({
        to: `${prefix}/search/$searchId`,
        params: { searchId },
        ...(candidateId ? { search: { candidate: candidateId } } : {}),
      });
    },
    [navigate, prefix],
  );

  // Page destinations — the palette NAVIGATES, not just searches. Overview + the three tiers + Activity live
  // in both trees; Needs attention + Team are admin-only surfaces.
  const navItems = useMemo(() => {
    const base = [
      { label: "Overview", to: prefix || "/", Icon: LayoutGrid },
      { label: "C-Suite", to: `${prefix}/csuite`, Icon: Layers },
      { label: "VP / Director", to: `${prefix}/vp-director`, Icon: Layers },
      { label: "General", to: `${prefix}/general`, Icon: Layers },
      { label: "Activity", to: `${prefix}/activity`, Icon: Activity },
    ];
    if (prefix === "/admin-view") {
      base.push({ label: "Needs attention", to: "/admin-view/attention", Icon: AlertTriangle });
      base.push({ label: "Team", to: "/admin-view/team", Icon: Users });
    }
    return base;
  }, [prefix]);

  const goTo = useCallback(
    (to: string) => {
      onOpenChange(false);
      (navigate as unknown as (o: { to: string }) => void)({ to });
    },
    [navigate, onOpenChange],
  );

  const pushRecent = useCallback((entry: RecentEntry) => {
    setRecents((prev) => {
      const next = [entry, ...prev.filter((r) => !(r.kind === entry.kind && r.id === entry.id))].slice(0, RECENTS_MAX);
      writeRecents(next);
      return next;
    });
  }, []);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["client", "search-index"],
    queryFn: clientApi.searchIndex,
    staleTime: 60_000,
  });
  const roles = data?.roles ?? [];
  const candidates = data?.candidates ?? [];

  const q = query.trim().toLowerCase();
  const roleHits = useMemo(
    () =>
      q === ""
        ? []
        : roles.filter((r) =>
            `${r.title} ${r.function ?? ""} ${r.ownerName ?? ""} ${TIER_LABEL[r.tier]}`.toLowerCase().includes(q),
          ),
    [roles, q],
  );
  const candHits = useMemo(
    () =>
      q === ""
        ? []
        : candidates.filter((c) =>
            `${c.fullName} ${c.currentTitle ?? ""} ${c.currentEmployer ?? ""} ${c.searchTitle}`
              .toLowerCase()
              .includes(q),
          ),
    [candidates, q],
  );
  const navHits = useMemo(
    () => (q === "" ? [] : navItems.filter((n) => n.label.toLowerCase().includes(q))),
    [navItems, q],
  );

  const selectRole = (r: SearchIndexRole) => {
    pushRecent({ kind: "role", id: r.id, searchId: r.id, title: r.title, subtitle: roleSub(r) });
    onOpenChange(false);
    go(r.id);
  };
  const selectCandidate = (c: SearchIndexCandidate) => {
    pushRecent({ kind: "candidate", id: c.id, searchId: c.searchId, title: c.fullName, subtitle: candSub(c) });
    onOpenChange(false);
    go(c.searchId, c.id);
  };
  const selectRecent = (r: RecentEntry) => {
    onOpenChange(false);
    go(r.searchId, r.kind === "candidate" ? r.id : undefined);
  };

  return (
    <>
      {/* Icon-only, the SAME 32px chip as the nav tools — so search reads as one of the system's controls
          rather than a differently-shaped pill wedged among icons. The ⌘K shortcut still works globally and
          is surfaced in the tooltip; the palette's own input carries the full "Search…" affordance. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-keyshortcuts="Meta+K Control+K"
        aria-label="Search"
        title="Search (⌘K)"
        className="inline-flex size-8 items-center justify-center rounded-lg text-muted-foreground outline-none transition-all hover:bg-muted/60 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Search className="size-[18px] shrink-0" aria-hidden />
      </button>

      {/* Enter animation only — NO exit animation. The palette is always mounted (unlike the candidate
          drawer, which unmounts wholesale), so it relies on Radix Presence to unmount on close. Selecting
          a result navigates synchronously in the same handler, which interrupts an exit animation and
          leaves the overlay stuck (a full-screen invisible scrim that eats clicks). With no
          `data-[state=closed]` animation, Radix unmounts the overlay instantly on close — clean, and the
          snappy vanish is the right feel for a palette anyway. */}
      <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=open]:fade-in" />
          <DialogPrimitive.Content
            aria-describedby={undefined}
            className="fixed left-1/2 top-[12vh] z-50 w-[560px] max-w-[92vw] -translate-x-1/2 overflow-hidden rounded-xl border bg-popover text-popover-foreground shadow-2xl outline-none data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-top-2 data-[state=open]:duration-150"
          >
            <DialogPrimitive.Title className="sr-only">Search roles and candidates</DialogPrimitive.Title>
            <Command label="Search roles and candidates" shouldFilter={false} className="flex flex-col">
              <div className="flex items-center gap-2 border-b px-3">
                <Search className="size-4 shrink-0 text-muted-foreground" />
                <Command.Input
                  value={query}
                  onValueChange={setQuery}
                  placeholder="Search roles and candidates…"
                  className="h-12 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                />
              </div>

              <Command.List className="max-h-[min(60vh,420px)] overflow-y-auto overscroll-contain p-2 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-muted-foreground">
                {isLoading ? (
                  <p className="px-2 py-6 text-center text-sm text-muted-foreground">Loading…</p>
                ) : isError ? (
                  <p className="px-2 py-6 text-center text-sm text-muted-foreground">Couldn't load search.</p>
                ) : q === "" ? (
                  // Resting state is a launchpad, never a dead-end sentence: jump to any page, plus recents.
                  <>
                    <Command.Group heading="Go to">
                      {navItems.map((n) => (
                        <Command.Item
                          key={n.to}
                          value={`nav-${n.label}`}
                          onSelect={() => goTo(n.to)}
                          className={ITEM_CLS}
                        >
                          <n.Icon className="size-4 shrink-0 text-muted-foreground" />
                          <span className="min-w-0 flex-1 truncate font-medium text-foreground">{n.label}</span>
                        </Command.Item>
                      ))}
                    </Command.Group>
                    {recents.length > 0 ? (
                      <Command.Group heading="Recent">
                        {recents.map((r) => {
                          const Icon = r.kind === "role" ? Briefcase : User;
                          return (
                            <Command.Item
                              key={`recent-${r.kind}-${r.id}`}
                              value={`recent-${r.kind}-${r.id}`}
                              onSelect={() => selectRecent(r)}
                              className={ITEM_CLS}
                            >
                              <Icon className="size-4 shrink-0 text-muted-foreground" />
                              <span className="min-w-0 flex-1 truncate font-medium text-foreground">{r.title}</span>
                              <span className="shrink-0 truncate text-xs text-muted-foreground">{r.subtitle}</span>
                            </Command.Item>
                          );
                        })}
                      </Command.Group>
                    ) : null}
                  </>
                ) : (
                  <>
                    <Command.Empty className="px-2 py-6 text-center text-sm text-muted-foreground">
                      No matches for “{query.trim()}”.
                    </Command.Empty>
                    {navHits.length > 0 ? (
                      <Command.Group heading="Go to">
                        {navHits.map((n) => (
                          <Command.Item
                            key={n.to}
                            value={`nav-${n.label}`}
                            onSelect={() => goTo(n.to)}
                            className={ITEM_CLS}
                          >
                            <n.Icon className="size-4 shrink-0 text-muted-foreground" />
                            <span className="min-w-0 flex-1 truncate font-medium text-foreground">{n.label}</span>
                          </Command.Item>
                        ))}
                      </Command.Group>
                    ) : null}
                    {roleHits.length > 0 ? (
                      <Command.Group heading="Roles">
                        {roleHits.map((r) => (
                          <Command.Item
                            key={r.id}
                            value={`role-${r.id}`}
                            onSelect={() => selectRole(r)}
                            className={ITEM_CLS}
                          >
                            <Briefcase className="size-4 shrink-0 text-muted-foreground" />
                            <span className="min-w-0 flex-1 truncate font-medium text-foreground">{r.title}</span>
                            <span className="shrink-0 truncate text-xs text-muted-foreground">{roleSub(r)}</span>
                          </Command.Item>
                        ))}
                      </Command.Group>
                    ) : null}
                    {candHits.length > 0 ? (
                      <Command.Group heading="Candidates">
                        {candHits.map((c) => (
                          <Command.Item
                            key={`${c.id}-${c.searchId}`}
                            value={`cand-${c.id}-${c.searchId}`}
                            onSelect={() => selectCandidate(c)}
                            className={ITEM_CLS}
                          >
                            <User className="size-4 shrink-0 text-muted-foreground" />
                            <div className="min-w-0 flex-1">
                              <div className="truncate font-medium text-foreground">{c.fullName}</div>
                              <div className="truncate text-xs text-muted-foreground">{candSub(c)}</div>
                            </div>
                            <span className="shrink-0 truncate text-xs text-muted-foreground">→ {c.searchTitle}</span>
                          </Command.Item>
                        ))}
                      </Command.Group>
                    ) : null}
                  </>
                )}
              </Command.List>

              <div className="flex items-center gap-4 border-t px-3 py-2 text-[11px] text-muted-foreground">
                <span className="flex items-center gap-1">
                  <KbdKey>↑</KbdKey>
                  <KbdKey>↓</KbdKey>
                  navigate
                </span>
                <span className="flex items-center gap-1">
                  <KbdKey>
                    <CornerDownLeft className="size-3" />
                  </KbdKey>
                  open
                </span>
                <span className="ml-auto flex items-center gap-1">
                  <KbdKey>esc</KbdKey>
                  close
                </span>
              </div>
            </Command>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </>
  );
}

function KbdKey({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="grid h-4 min-w-4 place-items-center rounded border bg-muted px-1 font-sans text-[10px] leading-none text-muted-foreground">
      {children}
    </kbd>
  );
}
